/**
 * Family key generation + ECDH wrap/unwrap.
 *
 * The "family key" is an AES-GCM-256 CryptoKey known to every member of a
 * family. It encrypts user data (children names, event titles/notes, calendar
 * comments). It is generated once by the first device in a family and then
 * propagated to every other device of every family member by ECDH wrap.
 *
 * Wrap protocol:
 *   1. Sender derives an AES-KW CryptoKey from (mySenderPrivate, theirRecipientPublic)
 *      via ECDH.
 *   2. Sender exports the family key as raw bytes (32 bytes) and wraps it
 *      with that AES-KW key.
 *   3. Sender uploads {iv, wrapped, senderDeviceId} to Convex `familyKeys`.
 *   4. Recipient fetches the row, looks up sender's public key in `userDevices`,
 *      derives the same AES-KW key from (theirPrivate, senderPublic), unwraps.
 *
 * The AES-KW key is deterministic for a (sender, recipient) pair so it can be
 * re-derived on every read. We could use AES-GCM with a random IV for forward
 * secrecy of the wrap itself, but the family key never rotates (yet), so it
 * buys us nothing. AES-KW is the standard wrap primitive.
 */

import { FAMILY_KEY_ALGO, bytesToBase64, base64ToBytes } from "./encryption";

const WRAP_ALGO: AesKeyAlgorithm = { name: "AES-KW", length: 256 };

/**
 * Generate a fresh AES-GCM-256 family key.
 * Extractable: true — we must be able to export it for wrapping.
 */
export const generateFamilyKey = async (): Promise<CryptoKey> => {
  return crypto.subtle.generateKey(FAMILY_KEY_ALGO, true, ["encrypt", "decrypt"]);
};

/**
 * Derive the AES-KW wrapping key shared between two devices via ECDH.
 * Used both to wrap (sender side) and unwrap (recipient side).
 */
const deriveWrapKey = async (
  myPrivate: CryptoKey,
  theirPublic: CryptoKey
): Promise<CryptoKey> => {
  return crypto.subtle.deriveKey(
    { name: "ECDH", public: theirPublic },
    myPrivate,
    WRAP_ALGO,
    false,
    ["wrapKey", "unwrapKey"]
  );
};

export interface WrappedFamilyKey {
  // base64 of the wrapped raw AES key (40 bytes for AES-256 with AES-KW)
  wrapped: string;
}

/**
 * Wrap the family key so that the recipient device can unwrap it.
 * Returns a serialized JSON string ready to put in Convex `familyKeys.encryptedKey`.
 */
export const wrapFamilyKeyForDevice = async (
  familyKey: CryptoKey,
  myPrivate: CryptoKey,
  theirPublic: CryptoKey
): Promise<string> => {
  const wrapKey = await deriveWrapKey(myPrivate, theirPublic);
  const wrappedBuffer = await crypto.subtle.wrapKey("raw", familyKey, wrapKey, WRAP_ALGO);
  const payload: WrappedFamilyKey = { wrapped: bytesToBase64(wrappedBuffer) };
  return JSON.stringify(payload);
};

/**
 * Unwrap a family key that was wrapped for THIS device by another device.
 */
export const unwrapFamilyKey = async (
  serialized: string,
  myPrivate: CryptoKey,
  senderPublic: CryptoKey
): Promise<CryptoKey> => {
  const payload = JSON.parse(serialized) as WrappedFamilyKey;
  if (!payload?.wrapped) throw new Error("Invalid wrapped family key payload");

  const wrapKey = await deriveWrapKey(myPrivate, senderPublic);
  const wrappedBytes = base64ToBytes(payload.wrapped);

  return crypto.subtle.unwrapKey(
    "raw",
    wrappedBytes,
    wrapKey,
    WRAP_ALGO,
    FAMILY_KEY_ALGO,
    true, // extractable so we can re-wrap for further devices
    ["encrypt", "decrypt"]
  );
};
